/**
 * @file CompanyAdminDashboardPage.tsx
 * @brief Landing page for company admins — scoped KPI overview + charts.
 *
 * @details
 * Shows who used Verblizr this month, month-to-date sessions and spend
 * (each vs the same days last month),
 * and a 30-day session/spend trend for the signed-in admin's company.
 * Layout uses GridBlock section cells flush to the content rail.
 *
 * @references
 * - BACKEND/src/features/company/routes.js
 * - BACKEND/src/features/sessions/routes.js
 * - BACKEND/src/features/invoices/routes.js
 *
 * @author DES
 * @date 26-05-2026
 * @updated 29-07-2026 — section grid layout
 * @updated 03-10-2026: KPIs = used this month, sessions, spend; deltas vs
 *          the same days last month; cells link to their pages
 */

import { Activity, CreditCard, UsersRound } from "lucide-react";
import {
  GridBand,
  GridBlock,
  GridCell,
  GridCells,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import { StatCard } from "@/features/analytics/components/StatCard";
import { TrendChart } from "@/features/analytics/components/TrendChart";
import { useAuth } from "@/shared/auth/auth-context";
import { formatMoneyMinor } from "@/shared/utils/formatters";
import { useMemo } from "react";
import { useUsageByEmployeeReport } from "@/features/company-reports/api";
import {
  useCompanyEmployeeStats,
  useCompanyOpenBalance,
  useCompanySessionTrend,
  useCompanySessionsMtd,
  useCompanySessionsPrevMtd,
  useCompanySpendPrevMtd,
} from "./api";
import { monthStartIso, nowEndIso } from "./dateRanges";
import { percentChange } from "./kpiCompare";

export function CompanyAdminDashboardPage() {
  const { user } = useAuth();
  const companyName = user?.company_name || "your company";

  const employees = useCompanyEmployeeStats();
  const sessionsMtd = useCompanySessionsMtd();
  const openBalance = useCompanyOpenBalance();
  const sessionTrend = useCompanySessionTrend();
  const sessionsPrev = useCompanySessionsPrevMtd();
  const spendPrev = useCompanySpendPrevMtd();
  // Who used it this month (03-10-2026, DES): anyone with an interpretation,
  // meeting or note in the month. The same per-employee report as Reports.
  const monthRange = useMemo(
    () => ({ startDate: monthStartIso(), endDate: nowEndIso() }),
    [],
  );
  const usage = useUsageByEmployeeReport(monthRange);
  const usedThisMonth = (usage.data ?? []).filter(
    (r) =>
      Number(r.interpretation_sessions || 0) +
        Number(r.meeting_count || 0) +
        Number(r.note_count || 0) >
      0,
  ).length;
  const activeEmployees = Number(
    employees.data?.active ?? employees.data?.total ?? 0,
  );

  const sessionsDelta =
    sessionsMtd.data != null && sessionsPrev.data != null
      ? percentChange(sessionsMtd.data, sessionsPrev.data)
      : undefined;
  const spend = openBalance.data;
  // Compared only in the same currency; a mixed or changed currency would
  // make the % meaningless.
  const spendDelta =
    spend && spendPrev.data && spend.currency === spendPrev.data.currency
      ? percentChange(spend.amountCents, spendPrev.data.amountCents)
      : undefined;
  const spendHint = spend
    ? spend.openAmountCents > 0
      ? `${formatMoneyMinor(spend.openAmountCents, spend.currency)} outstanding`
      : spend.mixedCurrency
        ? "Primary currency only"
        : "vs the same days last month"
    : undefined;

  return (
    <div className={gridPageBleedClass("pb-6")}>
      <GridBlock>
        <GridBand>
          <h2 className="text-xl font-semibold tracking-tight">
            Welcome{user?.firstName ? `, ${user.firstName}` : ""}
          </h2>
          <p className="mt-1 max-w-xl text-sm text-muted-foreground">
            Snapshot of {companyName}.
          </p>
        </GridBand>

        <GridCells className="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {/* DES 03-10-2026: the three numbers an admin asks first (who uses
              it, how much, what it costs), each compared with the same days
              of last month and linking to its page. Outstanding balance
              shows only when something is owed. */}
          <GridCell padded={false}>
            <StatCard
              variant="cell"
              label="Used Verblizr this month"
              icon={UsersRound}
              tone="brand"
              value={`${usedThisMonth} of ${activeEmployees}`}
              hint={
                employees.data
                  ? `${activeEmployees} active employees`
                  : undefined
              }
              isLoading={employees.isLoading || usage.isLoading}
              to="/company/employees"
            />
          </GridCell>
          <GridCell padded={false}>
            <StatCard
              variant="cell"
              label="Sessions this month"
              icon={Activity}
              tone="success"
              value={Number(sessionsMtd.data ?? 0).toLocaleString()}
              hint="vs the same days last month"
              delta={sessionsDelta}
              isLoading={sessionsMtd.isLoading}
              to="/company/sessions"
            />
          </GridCell>
          <GridCell padded={false}>
            <StatCard
              variant="cell"
              label="Spend this month"
              icon={CreditCard}
              tone="warning"
              value={formatMoneyMinor(
                spend?.amountCents ?? 0,
                spend?.currency ?? "GBP",
              )}
              hint={spendHint}
              delta={spendDelta}
              isLoading={openBalance.isLoading}
              to="/company/invoices"
            />
          </GridCell>
        </GridCells>

        <GridCells className="grid-cols-1 lg:grid-cols-2">
          <GridCell>
            <TrendChart
              variant="cell"
              title="Sessions, last 30 days"
              subtitle="Daily volume for your company"
              data={sessionTrend.data ?? []}
              isLoading={sessionTrend.isLoading}
              height={240}
              series={[
                {
                  dataKey: "session_count",
                  label: "Sessions",
                  color: "hsl(var(--brand))",
                },
              ]}
            />
          </GridCell>
          <GridCell>
            <TrendChart
              variant="cell"
              title="Session spend, last 30 days"
              subtitle="Daily billed total (GBP)"
              data={sessionTrend.data ?? []}
              isLoading={sessionTrend.isLoading}
              height={240}
              series={[
                {
                  dataKey: "total_cost_cents",
                  label: "Spend",
                  color: "hsl(var(--warning))",
                  format: (v) => formatMoneyMinor(v, "GBP"),
                },
              ]}
            />
          </GridCell>
        </GridCells>
      </GridBlock>
    </div>
  );
}
